    // api/wagtail.js
    import querystring from 'querystring';
    import {
        keysToCamelFromSnake,
        keysToSnakeFromCamel,
    } from '../utils/caseconverters';

    /**
     * Runtime-safe base URL selector
     * - Server → absolute URL (WAGTAIL_API_URL)
     * - Browser → relative URL (NEXT_PUBLIC_WAGTAIL_API_URL)
     */
    const isServer = typeof window === 'undefined';

    const BASE_API_URL = isServer
        ? process.env.WAGTAIL_API_URL
        : process.env.NEXT_PUBLIC_WAGTAIL_API_URL;

    const NEXT_PUBLIC_API_URL = process.env.NEXT_PUBLIC_API_URL;

    /**
     * Timeout helper (currently unused but preserved)
     */
    function createTimeoutController(milliseconds = 15000) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), milliseconds);

        return {
            signal: controller.signal,
            clearTimeout: () => clearTimeout(timeoutId),
        };
    }

    // -------------------- PAGE FETCHING --------------------

    export async function getPage(path, params, options) {
        params = {
            htmlPath: path,
            ...(params || {}),
        };

        return await getRequest(`${BASE_API_URL}/page_by_path/`, params, options);
    }

    export async function getPasswordProtectedPage(
        restrictionId,
        pageId,
        params,
        options
    ) {
        return await postRequest(
            `${NEXT_PUBLIC_API_URL}/password_protected_page/${restrictionId}/${pageId}/`,
            params || {},
            options
        );
    }

    export async function getAllPages() {
        return await getRequest(`${BASE_API_URL}/page_relative_urls/`);
    }

    export async function getPagePreview(contentType, token, params, options) {
        params = {
            contentType,
            token,
            ...(params || {}),
        };

        return await getRequest(`${BASE_API_URL}/page_preview/`, params, options);
    }

    export async function getPrimaryPages() {
        const homepageRes = await getRequest(
            `${BASE_API_URL}/page_by_path/`,
            { htmlPath: '/' },
            { cache: 'no-store' }
        );

        return homepageRes.json.componentProps?.children || [];
    }

    export async function getSecondaryPages(primaryPageUrl) {
        const secondaryPagesRes = await getRequest(
            `${BASE_API_URL}/page_by_path/`,
            { htmlPath: primaryPageUrl }
        );

        return secondaryPagesRes.json.componentProps?.children || [];
    }

    export async function getLocales() {
        const res = await getRequest(
            `${BASE_API_URL}/locales/`,
            {},
            { cache: 'force-cache' }
        );

        return res.json;
    }

    export async function getPublicViewData(slug, params, options) {
        return await getRequest(
            `${BASE_API_URL}/external_view_data/${slug}/`,
            params,
            options
        );
    }

    export async function getViewData(slug, params, options) {
        return await getRequest(
            `${BASE_API_URL}/external_view_data/${slug}/`,
            params,
            options
        );
    }

    export async function getRedirect(path, params, options) {
        params = {
            htmlPath: path,
            ...(params || {}),
        };

        return await getRequest(
            `${BASE_API_URL}/v1/redirect_by_path/`,
            params,
            options
        );
    }

    // -------------------- CORE REQUEST HANDLER --------------------

    export async function getRequest(url, params = {}, options = {}) {
        params = keysToSnakeFromCamel(params);

        let headers = {
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        };

        const queryString = querystring.stringify(params);

        let fetchOptions = { headers };

        if (options.cache) {
            fetchOptions.cache = options.cache;
        }

        if (options.revalidate) {
            fetchOptions.next = {
                revalidate: options.revalidate,
            };
        }

        const finalUrl = queryString ? `${url}?${queryString}` : url;

        const res = await fetch(finalUrl, fetchOptions);

        if (res.status < 200 || res.status >= 300) {
            const error = new WagtailApiResponseError(res, finalUrl, params);
            error.response = res;
            throw error;
        }

        const json = await res.json();

        return {
            headers: res.headers,
            json: keysToCamelFromSnake(json),
        };
    }

    // -------------------- POST --------------------

    export async function postRequest(url, params = {}, options = {}) {
        params = keysToSnakeFromCamel(params);

        const headers = {
            'Content-Type': 'application/json',
            ...(options.headers || {}),
        };

        const res = await fetch(url, {
            method: 'POST',
            body: JSON.stringify(params),
            headers,
        });

        if (res.status < 200 || res.status >= 300) {
            const error = new WagtailApiResponseError(res, url, params);
            error.response = res;
            throw error;
        }

        const json = await res.json();

        return {
            headers: res.headers,
            json: keysToCamelFromSnake(json),
        };
    }

    // -------------------- ERROR CLASS --------------------

    export class WagtailApiResponseError extends Error {
        constructor(res, url, params) {
            super(
                `${res.statusText}. Url: ${url}. Params: ${JSON.stringify(params)}`
            );
            this.name = 'WagtailApiResponseError';
        }
    }